import type { TDocument } from '@ballerine/common';
import { FILE_MAX_SIZE_IN_BYTE } from '@ballerine/common';

import type { TValidator } from '../../../Validator';
import type { TBaseValidators } from '../../../Validator/types';
import type { IValidatorWrapperContext } from '../../providers/ValidatorWrapper/types';
import type { IDocumentSizeValidatorParams } from './types';
import { isValueAnEntity } from '../../helpers/is-value-an-entity/is-value-an-entity';
import { isValueBusinessId } from '../../helpers/is-value-business-id/is-value-business-id';
import { formatDocumentId } from '../../utils/format-document-id';

const biteToMbInteger = (bite: number) => {
  return bite / 1024 / 1024;
};

const isExceedsFileSize = (file: File, fileSizeLimit: number) => {
  if (fileSizeLimit && file.size > fileSizeLimit) {
    return true;
  }

  return false;
};

export const documentSizeValidator: TValidator<
  TDocument[],
  IDocumentSizeValidatorParams,
  TBaseValidators | 'documentSize',
  IValidatorWrapperContext
> = (value, params, schema, context) => {
  const { fileSizeLimit = FILE_MAX_SIZE_IN_BYTE } = params.value || {};
  const errorMessage =
    params.message || `File size must not exceed ${biteToMbInteger(fileSizeLimit)}MB`;

  let file: File | undefined;

  if (isValueBusinessId(value)) {
    const documentId = formatDocumentId({
      type: schema.metadata?.element?.params?.template?.type,
      category: schema.metadata?.element?.params?.template?.category,
      entityType: 'business',
      entityId: value,
    });

    file = context?._files[documentId];
  }

  if (isValueAnEntity(value)) {
    const documentId = formatDocumentId({
      type: schema.metadata?.element?.params?.template?.type,
      category: schema.metadata?.element?.params?.template?.category,
      entityType: 'ubo',
      entityId: value.ballerineEntityId || value.__id!,
    });

    file = context?._files[documentId];
  }

  if (file instanceof File && isExceedsFileSize(file, fileSizeLimit)) {
    throw new Error(errorMessage);
  }

  return true;
};
